/*
 * Solitaire — the page: three games on one table, the solver's verdict, hints,
 * deal numbers, statistics, the sheets. Rules live in core.js, cards and
 * gestures in table.js, the solver in a worker (solver.js).
 *
 * What the page keeps to:
 *   • a move is applied at once — animation only shows it, input never waits;
 *   • each of the three games keeps its own unfinished game (cards, clock, undo);
 *   • "cannot be won" is said only when the solver searched everything, and
 *     the way out stands next to the message;
 *   • a game finished with a hint is won, but sets no record (GameKit.stats).
 */
(function () {
    'use strict';

    var CFG = window.__SOL || {}, T = CFG.t || {};
    var SLUG = 'solitaire';
    var S, GK, root, tableEl, table, def, sess = null, game = 'klondike';
    var clock, stats, immersive;
    var P;                                   // preferences
    var okAt = -1;                           // history length at which the game was last known winnable
    var inputs = { tap: 0, drag: 0, key: 0 };
    var touched = false, over = false, welcomeOn = false, plainHintSaid = false;
    var cursor = null, picked = null, lastTap = null, noteHidden = '';
    var sheetOpen = null, clockWasRunning = false;
    var IMPOSSIBLE_FC = [11982, 146692, 186216, 455889, 495505, 512118, 517776, 781948];

    function $(id) { return document.getElementById(id); }
    function t(k, vars) {
        var s = T[k] == null ? k : String(T[k]);
        if (vars) for (var n in vars) if (Object.prototype.hasOwnProperty.call(vars, n)) s = s.split(':' + n).join(String(vars[n]));
        return s;
    }
    function ts() { return window.__toolSettings; }
    function load(k, fb) { try { return ts().load(SLUG, k, fb); } catch (_) { return fb; } }
    function save(k, v) { try { return ts().save(SLUG, k, v); } catch (_) { return false; } }
    function drop(k) { try { localStorage.removeItem('ts_' + SLUG + '_' + k); } catch (_) {} }
    function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }
    function say(msg) { try { if (window.__announce) window.__announce(msg); } catch (_) {} }
    function funnel(stage, data) { try { if (window.__aeFunnel) window.__aeFunnel(stage, data || {}); } catch (_) {} }
    function fail(err, where) { try { if (window.__handleError) window.__handleError(err, where, { say: t('share_fail') }); } catch (_) {} }

    // ── preferences ─────────────────────────────────────────────────────────
    var DEFAULTS = {
        tap: true, auto: 'safe', targets: true, lefty: false, xl: false, four: false, ranks: false,
        back: 'indigo', table: 'felt', speed: 'normal', win: 'cascade', timer: true, sound: false,
        verdict: true, sure: true, rules: {},
        variants: { klondike: { draw: 1, passes: 0, score: 'std' }, spider: { suits: 2 }, freecell: {} }
    };
    function readPrefs() {
        var saved = load('prefs', null) || {}, out = {}, k;
        for (k in DEFAULTS) out[k] = Object.prototype.hasOwnProperty.call(saved, k) ? saved[k] : DEFAULTS[k];
        out.variants = {
            klondike: S.games.klondike.variant((saved.variants || {}).klondike || DEFAULTS.variants.klondike),
            spider: S.games.spider.variant((saved.variants || {}).spider || DEFAULTS.variants.spider),
            freecell: {}
        };
        return out;
    }
    function writePrefs() { save('prefs', P); }
    var SPEED = { slow: 420, normal: 220, fast: 110, off: 0 };
    function reduced() {
        try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
    }
    function speedMs() { return reduced() && P.speed === 'normal' ? 0 : SPEED[P.speed]; }
    // How the table shows a move: travel, a short fade where the card lands (the
    // system asks for less motion and the speed was left as it came), or nothing.
    function motion() {
        if (P.speed === 'off') return 'none';
        return reduced() && P.speed === 'normal' ? 'fade' : 'full';
    }
    function applyPrefs() {
        root.setAttribute('data-back', P.back);
        root.setAttribute('data-table', P.table);
        root.classList.toggle('sol-4c', !!P.four);
        root.classList.toggle('sol-xl', !!P.xl);
        root.classList.toggle('sol-no-tgt', !P.targets);
        root.style.setProperty('--ms', speedMs() + 'ms');
        $('solTimeBox').hidden = !P.timer;
        $('solV').hidden = !P.verdict;
        table.options({ lefty: P.lefty, xl: P.xl });
    }
    var LATIN = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    function rankText(r) {
        var loc = CFG.localRanks;
        if (P.ranks && loc) {
            if (r === 0) return loc[0];
            if (r >= 10) return loc[r - 9];
        }
        return LATIN[r];
    }

    // ── names for the screen reader ─────────────────────────────────────────
    function cardName(c) {
        if (c == null || c < 0) return t('pile_empty');
        if (!S.isUp(c)) return t('face_down');
        var ranks = t('ranks_full').split('|'), suits = t('suits_full').split('|');
        return t('card_fmt', { rank: ranks[S.rank(c)] || '', suit: suits[S.suit(c)] || '' });
    }
    function pileName(p) {
        var kind = def.kinds[p];
        if (kind === 'tab') return t('pile_col', { n: p - def.tab + 1 });
        return t('pile_' + kind);
    }

    // ── the solver ──────────────────────────────────────────────────────────
    var brain = {
        w: null, dead: false, job: null, seq: 0,
        verdict: 'wait', line: [], keys: null, timer: 0, idle: 0,
        next: {}                         // variantKey → {number, moves} : the next winnable deal, found ahead of time
    };
    function spawn() {
        if (brain.dead) return null;
        try {
            brain.w = new Worker(CFG.worker);
            brain.w.onmessage = onBrain;
            brain.w.onerror = function () { brainFailed('error'); };
        } catch (e) { brainFailed('construct'); }
        return brain.w;
    }
    function brainFailed(why) {
        if (brain.dead) return;
        brain.dead = true; brain.w = null;
        var job = brain.job; brain.job = null;
        try { if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'solver_worker_failed', why: why }); } catch (_) {}
        setVerdict('idk');
        if (job && job.cb) job.cb(null);
    }
    function ask(msg, cb, kind) {
        if (brain.dead) { cb(null); return; }
        // One question at a time: an answer nobody waits for any more is not worth its seconds.
        if (brain.job) {
            var old = brain.job;
            brain.job = null;
            try { brain.w.terminate(); } catch (_) {}
            brain.w = null;
            if (old.kind === 'find' && old.cb) old.cb(null);
        }
        if (!brain.w && !spawn()) { cb(null); return; }
        msg.id = ++brain.seq;
        brain.job = { id: msg.id, cb: cb, kind: kind };
        try { brain.w.postMessage(msg); } catch (e) { brainFailed('post'); }
    }
    function onBrain(e) {
        var d = e.data || {}, job = brain.job;
        if (!job || d.id !== job.id) return;
        brain.job = null;
        job.cb(d.error ? null : d);
    }
    function plainState() {
        var st = sess.state, out = {};
        for (var k in st) if (Object.prototype.hasOwnProperty.call(st, k)) out[k] = st[k];
        out.piles = st.piles.map(function (p) { return p.slice(); });
        return out;
    }
    function solveMs() { return game === 'spider' ? 2500 : (game === 'klondike' ? 1500 : 1200); }

    // The keys of every position along the solver's line: while the player
    // stays on the line the answer is already known.
    function setLine(moves) {
        brain.line = moves || [];
        brain.keys = new Map();
        if (!moves || !moves.length) return;
        var sim = S.clone(sess.state);
        brain.keys.set(S.key(sim), 0);
        for (var i = 0; i < moves.length; i++) {
            if (!def.legal(sim, moves[i])) { brain.line = moves.slice(0, i); break; }
            def.apply(sim, moves[i]);
            if (!brain.keys.has(S.key(sim))) brain.keys.set(S.key(sim), i + 1);
        }
    }
    function think() {
        clearTimeout(brain.timer); clearTimeout(brain.idle);
        brain.asking = false;
        if (!sess || over) return;
        var key = S.key(sess.state);
        if (brain.keys && brain.keys.has(key)) {
            var at = brain.keys.get(key);
            if (at > 0) setLineFrom(at);
            markOk();
            return;
        }
        brain.line = []; brain.keys = null;
        if (sess.steps() <= okAt && okAt >= 0) markOk(); else setVerdict('wait');
        if (!P.verdict && !pendingHint) return;
        var forKey = key;
        brain.asking = true;
        brain.timer = setTimeout(function () {
            ask({ type: 'solve', state: plainState(), maxMs: solveMs() }, function (res) {
                if (!sess || S.key(sess.state) !== forKey) return;
                brain.asking = false;
                if (!res) { setVerdict('idk'); flushHint(); return; }
                funnelVerdict(res);
                if (res.status === 'win') { setLine(res.moves); markOk(); }
                else if (res.status === 'lost') setVerdict('dead');
                else setVerdict(sess.steps() <= okAt && okAt >= 0 ? 'ok' : 'idk');
                flushHint();
                scheduleIdle();
            }, 'solve');
        }, 160);
    }
    function setLineFrom(at) {
        var rest = brain.line.slice(at);
        setLine(rest);
    }
    function markOk() {
        if (sess.steps() > okAt) okAt = sess.steps();
        setVerdict('ok');
        scheduleIdle();
    }
    var verdictSeen = {}, lastVerdict = null;
    function funnelVerdict(res) {
        // one sample per game and outcome, not one per move
        var tag = sess.number + '|' + res.status;
        if (verdictSeen[tag]) return;
        verdictSeen[tag] = 1;
        funnel('solver_verdict', { game: game, variant: sess.variantKey, status: res.status, ms: res.ms, nodes: res.nodes, step: sess.steps() });
    }
    function setVerdict(v) {
        var was = brain.verdict;
        brain.verdict = v;
        var el = $('solV'), txt = $('solVT');
        el.className = 'sol-v' + (v === 'ok' ? ' is-ok' : (v === 'dead' ? ' is-dead' : ''));
        txt.textContent = t(v === 'ok' ? 'v_ok' : (v === 'dead' ? 'v_dead' : (v === 'wait' ? 'v_checking' : 'v_idk')));
        // The verdict that CHANGED is said with a small jump: "checking…" in
        // between does not count, the first verdict of a game does not either.
        if (v !== 'wait') {
            if (lastVerdict && lastVerdict !== v && !el.hidden) pop(el, v === 'dead' ? 1.12 : 1.1);
            lastVerdict = v;
        }
        el.title = v === 'ok' ? t('v_ok_d') : (v === 'dead' ? t('v_dead_d') : (v === 'idk' ? t('v_idk_d') : ''));
        if (v === 'dead' && was !== 'dead') {
            funnel('dead_end', { game: game, variant: sess.variantKey, step: sess.steps(), back: okAt >= 0 ? sess.steps() - okAt : -1 });
            say(t('dead_text'));
        }
        showNote();
    }

    // While the player thinks, find the next winnable deal — "New game" is then instant.
    function scheduleIdle() {
        clearTimeout(brain.idle);
        if (brain.dead || !P.sure || !sess) return;
        var g = game, variant = sess.state.v, vk = sess.variantKey;
        if (g === 'freecell' || (g === 'spider' && variant.suits === 4)) return;
        if (brain.next[vk]) return;
        brain.idle = setTimeout(function () {
            if (brain.job || brain.next[vk]) return;
            ask({ type: 'find', game: g, variant: variant, from: randomNumber(g), tries: 40 }, function (res) {
                if (res && res.number) {
                    brain.next[vk] = { number: res.number, moves: res.moves };
                    var all = load('next', {}) || {};
                    all[vk] = res.number;
                    save('next', all);
                }
            }, 'prefetch');
        }, 1500);
    }

    // ── deal numbers ────────────────────────────────────────────────────────
    // A random deal is one of a million, like the numbered deals of Microsoft
    // FreeCell: six digits are a number people can read out and type. Longer
    // numbers (up to nine digits) still deal when typed.
    function randomNumber(g) {
        var max = 1000000, n = 0;
        void g;
        try {
            var buf = new Uint32Array(1);
            window.crypto.getRandomValues(buf);
            n = buf[0];
        } catch (_) { n = Math.floor(Date.now() * 7919 + performance.now() * 104729) >>> 0; }
        return 1 + (n % max);
    }
    function maxNumber(g) { return g === 'freecell' ? 2147483647 : S.MAX_NUMBER; }
    function knownImpossible(g, n) { return g === 'freecell' && IMPOSSIBLE_FC.indexOf(n) >= 0; }

    // ── a game on the table ─────────────────────────────────────────────────
    function variantOf(g) { return P.variants[g] || {}; }

    function putOnTable(how) {
        def = S.games[game];
        table.setGame(game, sess.state);
        cursor = null; picked = null; lastTap = null; noteHidden = '';
        table.render(sess.state, how || { instant: true });
        paintChrome();
        refresh();
    }
    function paintChrome() {
        var i, b = $('solGames').querySelectorAll('button');
        for (i = 0; i < b.length; i++) b[i].setAttribute('aria-pressed', b[i].getAttribute('data-game') === game ? 'true' : 'false');
        var seg = $('solVars'), v = sess.state.v, list = [];
        if (game === 'klondike') list = [['1', t('v_draw1'), v.draw === 1], ['3', t('v_draw3'), v.draw === 3]];
        else if (game === 'spider') list = [['1', t('v_suits1'), v.suits === 1], ['2', t('v_suits2'), v.suits === 2], ['4', t('v_suits4'), v.suits === 4]];
        seg.hidden = !list.length;
        seg.textContent = '';
        list.forEach(function (it) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.setAttribute('data-var', it[0]);
            btn.setAttribute('aria-pressed', it[2] ? 'true' : 'false');
            btn.textContent = it[1];
            seg.appendChild(btn);
        });
        $('solScoreBox').hidden = game === 'freecell';
        $('solDealL').textContent = t('deal_no', { n: sess.number });
        $('solDealS').textContent = t('deal_short', { n: sess.number });
    }

    function startGame(g, number, variant, opts) {
        opts = opts || {};
        settleUnfinished(g);
        game = g;
        sess = S.session(g, number, variant, { auto: P.auto, daily: opts.daily || null, sure: !!opts.sure });
        okAt = opts.sure ? 0 : -1;
        over = false; touched = !!opts.touched; plainHintSaid = false; lastVerdict = null; hud.score = null;
        inputs = { tap: 0, drag: 0, key: 0 };
        brain.line = []; brain.keys = null;
        clock.reset(0);
        hideFx();
        putOnTable(opts.instant ? { instant: true } : { deal: true });
        if (opts.line && opts.line.length) { setLine(opts.line); markOk(); } else think();
        if (!opts.quiet) {
            if (window.__stage) try { window.__stage('game_start'); } catch (_) {}
            funnel('game_start', { game: g, variant: sess.variantKey, sure: opts.sure ? 1 : 0, daily: opts.daily ? 1 : 0, via: opts.via || 'new' });
        }
        if (touched) persist(true);
        if (knownImpossible(g, sess.number)) toast(t('fc_impossible', { n: sess.number }), 'warning');
        armIdleNudge();
    }

    // A game started and left counts as played and lost — once.
    function settleUnfinished(nextGame) {
        if (!sess || over || nextGame !== game) return;
        if (!touched || !everMoved()) return;
        recordEnd(false);
    }
    function everMoved() { return sess.steps() > 0 || sess.canRedo() || sess.meta.undos > 0; }
    // later — the win's telemetry is sent once the cards have landed (see win())
    function recordEnd(won, later) {
        var ms = clock.elapsed(), score = finalScore(won, ms);
        var rec = {
            variant: sess.variantKey, won: won, ms: ms,
            score: game === 'freecell' ? sess.state.moves : score,
            lowerScoreWins: game === 'freecell',
            assisted: sess.meta.assisted,
            daily: won && sess.meta.daily ? sess.meta.daily : null
        };
        var out = stats.record(rec);
        var payload = {
            game: game, variant: sess.variantKey, won: won ? 1 : 0, ms: ms, moves: sess.state.moves,
            hints: sess.meta.hints, undos: sess.meta.undos, assisted: sess.meta.assisted ? 1 : 0,
            sure: sess.meta.sure ? 1 : 0, daily: sess.meta.daily ? 1 : 0,
            input: inputs.drag > inputs.tap && inputs.drag > inputs.key ? 'drag' : (inputs.key > inputs.tap ? 'key' : 'tap'),
            taps: inputs.tap, drags: inputs.drag, keys: inputs.key
        };
        var send = function () { try { if (window.__aeToolComplete) window.__aeToolComplete(payload); } catch (_) {} };
        if (won && later) out.send = send;
        else if (won) send();
        else funnel('game_abandoned', payload);
        return out;
    }
    function timeBonus(ms) {
        if (game !== 'klondike' || sess.state.v.score !== 'std') return 0;
        var sec = Math.floor(ms / 1000);
        return sec >= 30 ? Math.floor(700000 / sec) : 0;
    }
    function finalScore(won, ms) { return sess.score() + (won ? timeBonus(ms) : 0); }

    // ── saving ──────────────────────────────────────────────────────────────
    var saveTimer = 0;
    function persist(now) {
        if (!sess || !touched) return;
        clearTimeout(saveTimer);
        function write() {
            if (!sess) return;
            if (over) { drop('save_' + game); save('last', game); return; }
            save('save_' + game, { s: sess.serialize(), ms: clock.elapsed(), ok: okAt, in: inputs, at: Date.now() });
            save('last', game);
        }
        if (now) write(); else saveTimer = setTimeout(write, 250);
    }
    function restoreGame(g) {
        var data = load('save_' + g, null);
        if (!data || !data.s) return false;
        var s = S.restore(data.s);
        if (!s || s.won()) { drop('save_' + g); return false; }
        game = g; sess = s;
        sess.meta.auto = P.auto;
        okAt = typeof data.ok === 'number' ? Math.min(data.ok, sess.steps()) : -1;
        inputs = data.in && typeof data.in === 'object' ? { tap: +data.in.tap || 0, drag: +data.in.drag || 0, key: +data.in.key || 0 } : { tap: 0, drag: 0, key: 0 };
        over = false; touched = true; plainHintSaid = false; lastVerdict = null; hud.score = null;
        brain.line = []; brain.keys = null;
        clock.reset(+data.ms || 0);
        hideFx();
        putOnTable({ instant: true });
        think();
        funnel('game_restored', { game: g, variant: sess.variantKey, steps: sess.steps(), ms: +data.ms || 0 });
        return true;
    }

    // ── new games ───────────────────────────────────────────────────────────
    // "Winnable deals only" must not make anyone wait for a solver. So there
    // are three sources, fastest first: a deal the worker found while the last
    // game was played; a shipped list of deal numbers the solver has already
    // won (public/js/solitaire/deals.js); and only then a search on the spot.
    var finding = 0;
    // A number from a list of checked deals (or a FreeCell number that is not one of the eight)
    // is known to be winnable whoever typed it or sent it: the verdict need not wait for the solver.
    function isSure(g, n) {
        if (g === 'freecell') return n <= 1000000 && !knownImpossible(g, n);
        var list = sureList(S.games[g].variantKey(S.games[g].variant(variantOf(g))));
        return !!(list && list.indexOf(n) >= 0);
    }
    function sureList(vk) { return (S.SURE && S.SURE[vk] && S.SURE[vk].length) ? S.SURE[vk] : null; }
    function newGame(via) {
        var g = game, variant = variantOf(g), vk = S.games[g].variantKey(S.games[g].variant(variant));
        closeSheet();
        if (!P.sure) { startGame(g, randomNumber(g), variant, { sure: false, via: via, touched: true }); return; }
        if (g === 'freecell') {
            var n = randomNumber(g), guard = 0;
            while (knownImpossible(g, n) && guard++ < 20) n = randomNumber(g);
            startGame(g, n, variant, { sure: true, via: via, touched: true });
            return;
        }
        var ready = brain.next[vk], stored = (load('next', {}) || {})[vk];
        if (ready || stored) {
            delete brain.next[vk];
            var all = load('next', {}) || {};
            delete all[vk];
            save('next', all);
            var num = ready ? ready.number : S.cleanNumber(g, stored);
            if (num) { startGame(g, num, variant, { sure: true, line: ready ? ready.moves : null, via: via, touched: true }); return; }
        }
        var list = sureList(vk);
        if (list) {
            var pick = list[randomNumber(g) % list.length];
            if (sess && pick === sess.number && list.length > 1) pick = list[(list.indexOf(pick) + 1) % list.length];
            startGame(g, pick, variant, { sure: true, via: via, touched: true });
            return;
        }
        // A variant with no list (Vegas, a limited stock): look now, and say so.
        var mine = ++finding, from = randomNumber(g);
        clearTable(g);
        $('solWait').hidden = false;
        ask({ type: 'find', game: g, variant: variant, from: from, tries: 40 }, function (res) {
            if (mine !== finding) return;
            finding++;
            $('solWait').hidden = true;
            if (res && res.number) startGame(g, res.number, variant, { sure: true, line: res.moves, via: via, touched: true });
            else startGame(g, from, variant, { sure: false, via: via, touched: true });
        }, 'find');
    }
    // While a deal is being looked for the table shows the game's empty places, not the last game.
    function clearTable(g) {
        if (sess && g === game && !over) settleUnfinished(g);
        sess = null; game = g; def = S.games[g];
        var empty = { g: g, v: S.games[g].variant(variantOf(g)), n: 0, piles: [], score: 0, moves: 0 };
        for (var i = 0; i < def.piles; i++) empty.piles.push([]);
        table.setGame(g, empty);
        table.render(empty, { instant: true });
        $('solNote').hidden = true;
        var b = $('solGames').querySelectorAll('button');
        for (i = 0; i < b.length; i++) b[i].setAttribute('aria-pressed', b[i].getAttribute('data-game') === g ? 'true' : 'false');
    }
    function skipFinding() {
        if ($('solWait').hidden) return;
        finding++;
        $('solWait').hidden = true;
        if (brain.job && brain.job.kind === 'find') {
            brain.job = null;
            try { brain.w.terminate(); } catch (_) {}
            brain.w = null;
        }
        startGame(game, randomNumber(game), variantOf(game), { sure: false, via: 'skip', touched: true });
    }
    function restartDeal() {
        closeSheet();
        var n = sess.number, v = sess.state.v, sure = sess.meta.sure, daily = sess.meta.daily;
        startGame(game, n, v, { sure: sure, daily: daily, via: 'restart', touched: true });
    }
    function playNumber(n) {
        n = S.cleanNumber(game, n);
        var warn = $('solNumWarn');
        if (!n) { warn.textContent = t('new_number_bad', { max: maxNumber(game) }); warn.hidden = false; return; }
        warn.hidden = true;
        closeSheet();
        startGame(game, n, variantOf(game), { sure: isSure(game, n), via: 'number', touched: true });
    }
    // The deal of the day is the same number on every device: it is taken from
    // the shipped list by the day's seed, never from a search that a slow phone
    // and a fast desktop could end differently.
    function dailyNumber() {
        var key = GK.dailyKey(), variant = variantOf(game), vk = S.games[game].variantKey(S.games[game].variant(variant));
        var seed = GK.dailySeed(key, 'solitaire|' + vk), list = sureList(vk);
        var n;
        if (game === 'freecell') { n = 1 + (seed % 1000000); while (knownImpossible(game, n)) n++; }
        else n = list ? list[seed % list.length] : 1 + (seed % 999000000);
        return { number: n, key: key, sure: game === 'freecell' || !!list };
    }
    function playDaily() {
        closeSheet();
        var d = dailyNumber();
        funnel('daily', { game: game });
        startGame(game, d.number, variantOf(game), { sure: d.sure, daily: d.key, via: 'daily', touched: true });
    }
    function switchGame(g) {
        if (g === game || !S.games[g]) return;
        persist(true);
        skipFindingQuietly();
        funnel('game_switch', { from: game, to: g });
        if (!restoreGame(g)) { sess = null; game = g; def = S.games[g]; newGame('switch'); }
        save('last', g);
    }
    function skipFindingQuietly() { finding++; $('solWait').hidden = true; }
    function setVariant(value) {
        var v = variantOf(game), next;
        if (game === 'klondike') {
            next = S.games.klondike.variant({ draw: +value, passes: v.passes, score: v.score });
        } else if (game === 'spider') next = S.games.spider.variant({ suits: +value });
        else return;
        if (S.games[game].variantKey(next) === sess.variantKey) return;
        P.variants[game] = next;
        writePrefs();
        if (touched && everMoved() && !over) { openSheet('solNewSheet'); paintVarPending(value); return; }
        newGame('variant');
    }
    function paintVarPending(value) {
        var b = $('solVars').querySelectorAll('button');
        for (var i = 0; i < b.length; i++) b[i].setAttribute('aria-pressed', b[i].getAttribute('data-var') === String(value) ? 'true' : 'false');
    }

    // ── moves ───────────────────────────────────────────────────────────────
    // The counters answer a move: the count jumps, a change of score floats up
    // from it (after the cards have landed — the score is for what they did).
    var hud = { score: null, moves: null };
    function refresh(react) {
        var st = sess.state, sc = sess.score();
        $('solMoves').textContent = String(st.moves);
        $('solScore').textContent = String(sc);
        $('solUndo').disabled = !sess.canUndo() || over;
        $('solRedo').disabled = !sess.canRedo() || over;
        var fin = !over && def.finishLine(st);
        var finBtn = $('solFinish'), wasHidden = finBtn.hidden;
        finBtn.hidden = !(fin && fin.length > 1);
        if (react && hud.score !== null) {
            if (st.moves !== hud.moves) pop($('solMoves'), 1.18);
            var d = sc - hud.score;
            if (d && Math.abs(d) >= 5 && game !== 'freecell') floatDelta(d, react.delay || 0);
            // «Доиграть» appearing is news: it comes in with a jump too
            if (wasHidden && !finBtn.hidden) pop(finBtn, 1.12);
        }
        hud.score = sc; hud.moves = st.moves;
    }
    function animate(node, frames, opts) {
        if (!node || typeof node.animate !== 'function' || motion() !== 'full') return null;
        try { return node.animate(frames, opts); } catch (_) { return null; }
    }
    function pop(node, k) {
        animate(node, [{ transform: 'scale(1)' }, { transform: 'scale(' + (k || 1.15) + ')', offset: 0.35 }, { transform: 'scale(1)' }], { duration: 280, easing: 'ease-out' });
    }
    function floatDelta(d, delay) {
        var box = $('solScoreBox');
        if (!box || box.hidden || motion() !== 'full') return;
        var tag = document.createElement('span');
        tag.className = 'sol-delta' + (d < 0 ? ' neg' : '');
        tag.setAttribute('aria-hidden', 'true');
        tag.textContent = (d > 0 ? '+' : '\u2212') + Math.abs(d);
        box.appendChild(tag);
        var an = animate(tag, [
            { transform: 'translate(-50%,0)', opacity: 0 },
            { transform: 'translate(-50%,-6px)', opacity: 1, offset: 0.15 },
            { transform: 'translate(-50%,-22px)', opacity: 0 }
        ], { duration: 1000, delay: delay, easing: 'ease-out', fill: 'both' });
        if (an) an.onfinish = function () { if (tag.parentNode) tag.parentNode.removeChild(tag); };
        else if (tag.parentNode) tag.parentNode.removeChild(tag);
    }
    function describe(step) {
        var first = step[0], st = sess.state;
        if (first.k !== 'm') return first.k === 'r' ? t('hint_draw') : (game === 'spider' ? t('hint_deal') : cardName(topOf(1)));
        var pile = st.piles[first.t], base = pile[pile.length - first.n];
        var from = first.n > 1 ? t('a_cards', { n: first.n, card: cardName(base) }) : cardName(base === undefined ? topOf(first.t) : base);
        var msg = t('a_move', { card: from, to: pileName(first.t) });
        if (first.fl) msg += '. ' + t('a_flip', { card: cardName(topOf(first.f)) });
        if (first.runs) msg += '. ' + t('a_suit_done');
        return msg;
    }
    function topOf(p) { var pile = sess.state.piles[p]; return pile.length ? pile[pile.length - 1] : -1; }

    // opts.from — where a drag let the cards go; opts.plain — no beats, the table
    // flies the cards from where it shows them (a card re-sent to its other place).
    function play(mv, via, opts) {
        if (over || !sess) return false;
        opts = opts || {};
        var before = sess.steps(), pre = S.clone(sess.state);
        var step = sess.play(mv);
        if (!step) return false;
        if (before < okAt) okAt = before;
        inputs[via || 'tap'] = (inputs[via || 'tap'] || 0) + 1;
        if (!touched || !clock.running()) {
            if (!touched) funnel('first_move', { game: game, variant: sess.variantKey, via: via || 'tap' });
            touched = true; welcomeOn = false;
            clock.start();
        }
        afterChange(step, opts.plain ? {} : { beats: S.beats(game, pre, step), from: opts.from || null });
        sound(step[0].k === 'm' ? 'place' : 'turn');
        return true;
    }
    function afterChange(step, how) {
        table.clearHint();
        table.hideTip();
        setPicked(null);
        noteHidden = '';
        var ms = table.render(sess.state, how || {});
        if (cursor) table.setCursor(cursor.p, cursorDepth());
        refresh({ delay: Math.min(ms, 1400) });
        if (step) say(describe(step));
        if (sess.won()) { win(); return; }
        think();
        persist();
    }
    function undo(viaNote) {
        if (over || !sess.canUndo()) return;
        var step = sess.undo();
        // the step flies back the way it came, last beat first
        afterChange(null, { beats: S.beats(game, S.clone(sess.state), step), reverse: true });
        say(t('a_undo'));
        funnel('undo', { game: game, dead: viaNote ? 1 : 0 });
    }
    function redo() {
        if (over || !sess.canRedo()) return;
        var pre = S.clone(sess.state), step = sess.redo();
        afterChange(step, { beats: S.beats(game, pre, step) });
        say(t('a_redo'));
    }
    // Back to the last position the solver knew could be won.
    function rewind() {
        if (over || okAt < 0 || okAt >= sess.steps()) return;
        var n = 0;
        while (sess.steps() > okAt && sess.canUndo()) { sess.undo(); n++; }
        sess.meta.assisted = true;
        afterChange(null, { kind: 'move' });
        funnel('rewind_to_winnable', { game: game, steps: n });
    }
    function doStock(via) {
        var a = def.stockAction(sess.state);
        if (a.mv) { play(a.mv, via); return; }
        // the reason stands next to the stock, where the finger is — not in a toast across the page
        if (a.why === 'gaps') { table.shake(0, 1); stockTip(t('need_fill')); nudgeRules('gaps'); }
        else if (a.why === 'passes') stockTip(t('no_passes'));
    }
    function stockTip(text) {
        table.tip(0, 1, document.createTextNode(text), 2400 + text.length * 30);
        say(text);
    }
    // Everything that can go home goes — and the table shows it as a chain, one
    // card after another, each starting a little after the last.
    function collectAll() {
        if (over || game === 'spider') return;
        var n = 0, mv, guard = 0, pre = S.clone(sess.state), recs = [];
        while (guard++ < 60 && (mv = def.autoMove(sess.state, 'all'))) {
            var step = sess.play(mv, { noAuto: true });
            if (!step) break;
            recs = recs.concat(step);
            n++;
        }
        if (!n) return;
        touched = true; welcomeOn = false; clock.start();
        afterChange(null, { beats: S.beats(game, pre, recs), finish: true });
        say(t('collected', { n: n }));
    }
    // The rest of the game plays itself: every move is made now (each one its own
    // undo step, as before), the table plays them back as one readable chain.
    function finishGame() {
        if (over) return;
        var line = def.finishLine(sess.state);
        if (!line || !line.length) return;
        var pre = S.clone(sess.state), recs = [];
        touched = true; welcomeOn = false; clock.start();
        for (var i = 0; i < line.length; i++) {
            var step = sess.play(line[i], { noAuto: true });
            if (!step) break;
            recs = recs.concat(step);
        }
        if (!recs.length) return;
        funnel('auto_finish', { game: game, moves: recs.length });
        afterChange(null, { beats: S.beats(game, pre, recs), finish: true });
    }

    // ── tap, drag, keyboard ─────────────────────────────────────────────────
    function canLift(p, n) {
        if (over || !sess) return false;
        var kind = def.kinds[p], pile = sess.state.piles[p];
        if (kind === 'stock' || n < 1 || n > pile.length) return false;
        if (game === 'spider' && kind === 'found') return false;
        if (game === 'freecell' && kind === 'found') return false;
        return n <= def.runLength(sess.state, p);
    }
    function targetsOf(p, n) {
        var out = [];
        for (var i = 0; i < def.piles; i++) if (def.canDrop(sess.state, p, n, i)) out.push(i);
        return out;
    }
    function setPicked(sel) {
        picked = sel;
        table.setPicked(sel ? sel.p : null, sel ? sel.n : 0);
    }
    function pick(p, n) {
        if (!canLift(p, n)) { table.shake(p, n); return; }
        setPicked({ p: p, n: n });
        say(t('a_picked', { card: cardName(sess.state.piles[p][sess.state.piles[p].length - n]) }));
    }
    function dropPicked(p, via) {
        var sel = picked;
        if (!sel) return false;
        if (p === sel.p) { setPicked(null); say(t('a_dropped')); return true; }
        var to = p;
        // any foundation stands for the right one
        if (def.kinds[p] === 'found' && game !== 'spider' && sel.n === 1) {
            var c = sess.state.piles[sel.p][sess.state.piles[sel.p].length - 1];
            to = def.found + S.suit(c);
        }
        if (def.canDrop(sess.state, sel.p, sel.n, to)) { setPicked(null); return play({ k: 'm', f: sel.p, t: to, n: sel.n }, via); }
        return false;
    }
    function onTap(p, n, ev, via) {
        if (over || !sess) return;
        var kind = def.kinds[p], now = Date.now();
        if (kind === 'stock') { doStock(via); return; }
        if (picked) {
            if (dropPicked(p, via)) return;
            if (canLift(p, n)) pick(p, n); else { table.shake(p, n); setPicked(null); }
            return;
        }
        var pile = sess.state.piles[p], base = pile[pile.length - n];
        // The second click of a double-click lands on whatever lies under the
        // card the first click sent away. That click belongs to the first card.
        if (ev && ev.detail !== 0 && lastTap && lastTap.moved && now - lastTap.t < 350 &&
            Math.abs(ev.clientX - lastTap.x) < 16 && Math.abs(ev.clientY - lastTap.y) < 16 &&
            S.uid(base) !== lastTap.uid) return;
        if (!P.tap) { pick(p, n); return; }
        if (!canLift(p, n)) { nope(p, n); return; }
        // The same card tapped again right after it moved: it wanted the other place.
        if (lastTap && lastTap.moved && lastTap.uid === S.uid(base) && now - lastTap.t < 4000 && lastTap.steps === sess.steps() && lastTap.alts.length > 1) {
            var alts = lastTap.alts, at = (lastTap.at + 1) % alts.length, undone = sess.undo();
            if (undone) {
                sess.meta.undos--;
                if (play({ k: 'm', f: undone[0].f, t: alts[at], n: undone[0].n }, via, { plain: true })) {
                    lastTap = { t: now, x: ev ? ev.clientX : 0, y: ev ? ev.clientY : 0, uid: S.uid(base), moved: true, alts: alts, at: at, steps: sess.steps() };
                    return;
                }
                sess.redo();
                table.render(sess.state);
            }
            lastTap = null;
            return;
        }
        var list = def.tapTargets(sess.state, p, n);
        if (!list.length) { nope(p, n); lastTap = null; return; }
        var done = play({ k: 'm', f: p, t: list[0], n: n }, via);
        lastTap = done ? { t: now, x: ev ? ev.clientX : 0, y: ev ? ev.clientY : 0, uid: S.uid(base), moved: true, alts: list, at: 0, steps: sess.steps() } : null;
    }
    function nope(p, n) {
        table.shake(p, n);
        explain(p, n, -1);
    }

    // ── why a card did not move ─────────────────────────────────────────────
    // The words next to the card, with the cards it would need drawn as small
    // cards (core.js `why` decides; the sentences are js.why_* in the translations).
    var SUIT_INK = [0, 1, 1, 0];
    function chip(c) {
        var b = document.createElement('b'), s = S.suit(c);
        b.className = 'sol-cc' + (SUIT_INK[s] ? ' red' : '') + ' s' + s;
        var r = document.createElement('span');
        r.textContent = rankText(S.rank(c));
        b.appendChild(r);
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttribute('href', '#sol-s' + s);
        svg.appendChild(use);
        b.appendChild(svg);
        return b;
    }
    var SLOT = /:(card|need1|need2|rank|n)\b/g;
    function richText(tpl, w) {
        var frag = document.createDocumentFragment(), last = 0, m;
        SLOT.lastIndex = 0;
        while ((m = SLOT.exec(tpl))) {
            if (m.index > last) frag.appendChild(document.createTextNode(tpl.slice(last, m.index)));
            var name = m[1], node;
            if (name === 'card' && w.c != null) node = chip(w.c);
            else if (name === 'need1' && w.a != null) node = chip(w.a);
            else if (name === 'need2' && w.b != null) node = chip(w.b);
            else if (name === 'rank' && w.r != null) { node = document.createElement('b'); node.className = 'sol-cc'; node.textContent = rankText(w.r); }
            else node = document.createTextNode(name === 'n' && w.n != null ? String(w.n) : '');
            frag.appendChild(node);
            last = m.index + m[0].length;
        }
        if (last < tpl.length) frag.appendChild(document.createTextNode(tpl.slice(last)));
        return frag;
    }
    function plainText(tpl, w) {
        var ranks = t('ranks_full').split('|');
        return tpl.replace(SLOT, function (_, name) {
            if (name === 'card') return w.c != null ? cardName(w.c) : '';
            if (name === 'need1') return w.a != null ? cardName(w.a) : '';
            if (name === 'need2') return w.b != null ? cardName(w.b) : '';
            if (name === 'rank') return w.r != null ? (ranks[w.r] || '') : '';
            return w.n != null ? String(w.n) : '';
        });
    }
    function explain(p, n, to) {
        if (!sess) return;
        var w = S.why(game, sess.state, p, n, to) || { k: 'no_move' };
        var tpl = T[w.k] == null ? t('no_move') : String(T[w.k]);
        table.tip(to >= 0 ? to : p, to >= 0 ? 1 : n, richText(tpl, w), 2400 + tpl.length * 30);
        say(plainText(tpl, w));
        nudgeRules('nope');
        funnel('why', { game: game, k: w.k, drop: to >= 0 ? 1 : 0 });
    }
    function onSlot(p, ev, via) {
        if (over || !sess) return;
        if (def.kinds[p] === 'stock') { doStock(via); return; }
        if (picked) { if (!dropPicked(p, via)) { table.shake(p, 1); setPicked(null); } }
    }

    // keyboard: a cursor that walks the piles
    function order(row) {
        var list = [];
        for (var i = 0; i < def.piles; i++) if ((def.kinds[i] === 'tab') === (row === 1)) list.push(i);
        var slotX = function (p) { return parseFloat(table.slotOf(p).style.getPropertyValue('--x')) || 0; };
        var slotY = function (p) { return parseFloat(table.slotOf(p).style.getPropertyValue('--yk')) || 0; };
        list.sort(function (a, b) { return slotX(a) - slotX(b) || slotY(a) - slotY(b); });
        return list;
    }
    function cursorDepth() {
        if (!cursor) return 1;
        var len = sess.state.piles[cursor.p].length;
        return Math.max(1, Math.min(cursor.n, Math.max(1, len)));
    }
    function moveCursor(dx, dy) {
        if (!cursor) cursor = { p: def.tab, n: 1 };
        var row = def.kinds[cursor.p] === 'tab' ? 1 : 0, list = order(row), at = list.indexOf(cursor.p);
        if (dx) { cursor.p = list[(at + dx + list.length) % list.length]; cursor.n = 1; }
        if (dy) {
            var run = Math.max(1, def.runLength(sess.state, cursor.p));
            // In a column Up walks into the run, and from its first card up to
            // the row above; Down walks back out. The top row only goes down.
            if (row === 1 && dy < 0 && cursor.n < run) cursor.n++;
            else if (row === 1 && dy > 0 && cursor.n > 1) cursor.n--;
            else if ((row === 1 && dy > 0) || (row === 0 && dy < 0)) { /* the edge of the table */ }
            else {
                var other = order(1 - row), share = at / Math.max(1, list.length - 1);
                cursor.p = other[Math.round(share * (other.length - 1))];
                cursor.n = 1;
            }
        }
        cursor.n = cursorDepth();
        table.setCursor(cursor.p, cursor.n);
        var pile = sess.state.piles[cursor.p];
        say(t('a_cursor', { pile: pileName(cursor.p), card: cardName(pile.length ? pile[pile.length - cursor.n] : -1) }));
    }
    function onKey(e) {
        if (sheetOpen || !sess) return;
        if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
        var k = e.key, used = true;
        // Letters by the key, not by the character: on a Russian layout H types «р»,
        // and the shortcuts must work whatever the keyboard is switched to.
        var low = /^Key[A-Z]$/.test(e.code || '') ? e.code.slice(3).toLowerCase() : (k.length === 1 ? k.toLowerCase() : k);
        var mod = e.ctrlKey || e.metaKey, onTable = e.target === tableEl;
        if (e.altKey) return;
        if (!mod && (k === '?' || k === 'F1')) { e.preventDefault(); openRules('key'); return; }
        // Arrows, Enter and Space belong to the table only while the table has the focus: on a button they are the button's.
        if (!onTable && !mod && (k.indexOf('Arrow') === 0 || k === 'Enter' || k === ' ' || k === 'Spacebar')) return;
        if (mod) {
            if (low === 'z' && !e.shiftKey) undo();
            else if (low === 'y' || (low === 'z' && e.shiftKey)) redo();
            else used = false;
        } else if (k === 'ArrowLeft') moveCursor(-1, 0);
        else if (k === 'ArrowRight') moveCursor(1, 0);
        else if (k === 'ArrowUp') moveCursor(0, -1);
        else if (k === 'ArrowDown') moveCursor(0, 1);
        else if (k === 'Enter') {
            if (!cursor) moveCursor(0, 0);
            else if (picked) { if (!dropPicked(cursor.p, 'key')) table.shake(cursor.p, 1); }
            else if (sess.state.piles[cursor.p].length || def.kinds[cursor.p] === 'stock') onTap(cursor.p, cursorDepth(), null, 'key');
            else onSlot(cursor.p, null, 'key');
        } else if (k === ' ' || k === 'Spacebar') {
            if (!cursor) moveCursor(0, 0);
            else if (picked) { if (!dropPicked(cursor.p, 'key')) table.shake(cursor.p, 1); }
            else if (def.kinds[cursor.p] === 'stock') doStock('key');
            else pick(cursor.p, cursorDepth());
        } else if (k === 'Escape') {
            if (picked) { setPicked(null); say(t('a_dropped')); } else used = false;
        } else if (low === 'd') doStock('key');
        else if (low === 'z' || low === 'u') undo();
        else if (low === 'y') redo();
        else if (low === 'h') hint();
        else if (low === 'n') openNew();
        else if (low === 'a') collectAll();
        else if (low === 'f') toggleFull();
        else used = false;
        if (used) e.preventDefault();
    }

    // ── hint ────────────────────────────────────────────────────────────────
    var pendingHint = false;
    function hint() {
        if (over || !sess) return;
        sess.meta.hints++;
        sess.meta.assisted = true;
        funnel('hint', { game: game, verdict: brain.verdict, step: sess.steps() });
        // The solver may be a moment away from its answer: the hint waits for it rather than guess.
        if ((brain.asking || brain.verdict === 'wait') && !brain.dead) {
            pendingHint = true;
            if (!P.verdict) think();
            // the answer may never come (a worker that died mid-question): do not leave the button silent
            setTimeout(flushHint, solveMs() + 900);
            return;
        }
        giveHint();
        persist();
    }
    function flushHint() {
        if (!pendingHint) return;
        pendingHint = false;
        giveHint();
    }
    function giveHint() {
        var mv = null, plain = false;
        if (brain.verdict === 'dead') { toast(t('hint_dead'), 'warning'); noteHidden = ''; showNote(); return; }
        if (brain.line.length && brain.keys && brain.keys.get(S.key(sess.state)) === 0) mv = brain.line[0];
        if (!mv) {
            var list = def.ranked(sess.state);
            if (list.length) { mv = list[0].mv; plain = true; }
        }
        if (!mv) { toast(t('hint_none'), 'warning'); return; }
        table.showHint(mv);
        if (mv.k !== 'm') say(game === 'spider' ? t('hint_deal') : t('hint_draw'));
        else say(t('a_move', { card: cardName(sess.state.piles[mv.f][sess.state.piles[mv.f].length - mv.n]), to: pileName(mv.t) }));
        if (plain && !plainHintSaid && !brain.dead) { plainHintSaid = true; toast(t('hint_plain'), 'info'); }
    }

    // ── the bar that says "no way from here" ────────────────────────────────
    function showNote() {
        if (!sess) return;
        var note = $('solNote'), dead = brain.verdict === 'dead' && P.verdict, stuck = !over && def.stuck(sess.state);
        var tag = (dead ? 'd' : '') + (stuck ? 's' : '') + sess.steps();
        if (over || (!dead && !stuck) || noteHidden === tag) { note.hidden = true; return; }
        $('solNoteT').textContent = stuck && !dead ? t('stuck_text') : t('dead_text');
        $('solRewind').hidden = !(okAt >= 0 && okAt < sess.steps());
        $('solNoteUndo').hidden = !sess.canUndo();
        note.hidden = false;
        note._tag = tag;
    }

    // ── win ─────────────────────────────────────────────────────────────────
    function win() {
        over = true;
        clock.pause();
        // The result is recorded now; what the page says and sends about it waits for the
        // cards still flying home: the site's «tool complete» signal measures the page
        // (forced layouts), and in the middle of «Finish» that stalled the next tap.
        var ms = clock.elapsed(), bonus = timeBonus(ms), out = recordEnd(true, true), sent = false;
        function announce() {
            if (sent) return;
            sent = true;
            if (out.send) out.send();
            say(t('a_won'));
            sound('win');
        }
        setTimeout(announce, 8000);
        persist(true);
        table.clearHint();
        setVerdict('ok');
        $('solNote').hidden = true;
        refresh();
        var rows = [];
        if (P.timer) rows.push([GK.formatTime(ms), t('s_time')]);
        rows.push([String(sess.state.moves), t('s_moves')]);
        if (game !== 'freecell') rows.push([String(finalScore(true, ms)), t('s_score')]);
        if (bonus) rows.push(['+' + bonus, t('win_bonus')]);
        if (out.summary.streak > 1) rows.push([String(out.summary.streak), t('st_streak')]);
        fillStats($('solWinStats'), rows);
        var notes = [];
        if (sess.meta.assisted) notes.push(t('win_assisted'));
        else {
            if (out.bestTime && P.timer) notes.push(t('win_best_time'));
            if (out.bestScore && game !== 'freecell') notes.push(t('win_best_score'));
        }
        $('solWinNote').textContent = notes.join(' ');
        $('solWinNote').hidden = !notes.length;
        var show = function () { if (over) openSheet('solWinSheet'); };
        // the last card lands first; then the four (eight) homes take a bow, then the finish
        table.idle(function () {
            announce();
            if (!over) return;
            var w = table.wave();
            setTimeout(function () {
                if (!over) return;
                // a machine that could not keep up with the cards gets the calm finish
                if (P.win === 'cascade' && motion() === 'full' && !table.isLight()) cascade(show); else setTimeout(show, 200);
            }, w ? w + 120 : 150);
        });
    }

    // The bouncing-cards finish. Drawn on a canvas over the table; any tap ends it.
    var fx = { raf: 0, stop: null }, suitPaths = {};
    function suitPath(s) {
        if (suitPaths[s] === undefined) {
            suitPaths[s] = null;
            try {
                var node = document.querySelector('#sol-s' + s + ' path');
                if (node && window.Path2D) suitPaths[s] = new Path2D(node.getAttribute('d'));
            } catch (_) {}
        }
        return suitPaths[s];
    }
    function hideFx() {
        if (fx.raf) cancelAnimationFrame(fx.raf);
        fx.raf = 0;
        var c = $('solFx');
        c.hidden = true;
        if (fx.stop) { c.removeEventListener('click', fx.stop); fx.stop = null; }
    }
    function cascade(done) {
        var c = $('solFx'), M = table.metrics(), dpr = Math.min(2, window.devicePixelRatio || 1);
        var W = tableEl.clientWidth, H = tableEl.clientHeight;
        if (!W || !H || !c.getContext) { done(); return; }
        c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
        c.hidden = false;
        var ctx = c.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        var cw = M.cw, ch = M.ch, tr = tableEl.getBoundingClientRect(), queue = [], live = [], i, j;
        var css = getComputedStyle(root);
        var inks = [css.getPropertyValue('--ink-blk').trim() || '#15171c', css.getPropertyValue('--ink-red').trim() || '#c1121f'];
        if (P.four) inks = [inks[0], inks[1], css.getPropertyValue('--ink-blue').trim() || '#1257c2', css.getPropertyValue('--ink-green').trim() || '#0a7a3a'];
        // every card of the finished foundations, kings first
        for (j = 12; j >= 0; j--) {
            for (i = 0; i < def.piles; i++) {
                if (def.kinds[i] !== 'found') continue;
                var pile = sess.state.piles[i], card = pile[game === 'spider' ? 12 - j : j];
                if (card === undefined) continue;
                var r = table.slotOf(i).getBoundingClientRect();
                queue.push({ c: card, x: r.left - tr.left, y: r.top - tr.top });
            }
        }
        function ink(card) {
            var s = S.suit(card);
            if (P.four) return s === 2 ? inks[2] : (s === 3 ? inks[3] : (s === 1 ? inks[1] : inks[0]));
            return s === 1 || s === 2 ? inks[1] : inks[0];
        }
        function drawCard(o) {
            var x = o.x, y = o.y, rad = cw * 0.09;
            ctx.beginPath();
            ctx.moveTo(x + rad, y); ctx.lineTo(x + cw - rad, y); ctx.quadraticCurveTo(x + cw, y, x + cw, y + rad);
            ctx.lineTo(x + cw, y + ch - rad); ctx.quadraticCurveTo(x + cw, y + ch, x + cw - rad, y + ch);
            ctx.lineTo(x + rad, y + ch); ctx.quadraticCurveTo(x, y + ch, x, y + ch - rad);
            ctx.lineTo(x, y + rad); ctx.quadraticCurveTo(x, y, x + rad, y);
            ctx.closePath();
            ctx.fillStyle = '#fcfbf7'; ctx.fill();
            ctx.strokeStyle = 'rgba(0,0,0,.45)'; ctx.lineWidth = 1; ctx.stroke();
            ctx.fillStyle = ink(o.c);
            ctx.font = '800 ' + Math.max(12, cw * 0.33) + 'px system-ui, sans-serif';
            ctx.textBaseline = 'top';
            ctx.fillText(rankText(S.rank(o.c)), x + cw * 0.07, y + ch * 0.04);
            var shape = suitPath(S.suit(o.c));
            if (shape) {
                var k = cw * 0.5 / 24;
                ctx.save();
                ctx.translate(x + cw / 2 - 12 * k, y + ch * 0.62 - 12 * k);
                ctx.scale(k, k);
                ctx.fill(shape);
                ctx.restore();
            }
        }
        var t0 = performance.now(), launched = 0, lastLaunch = -1e9, ended = false, seed = GK.rng(sess.number);
        function end() {
            if (ended) return;
            ended = true;
            hideFx();
            done();
        }
        fx.stop = end;
        c.addEventListener('click', end);
        var prev = t0;
        function frame(now) {
            if (ended) return;
            var dt = Math.min(34, now - prev) / 16.7;
            prev = now;
            if (launched < queue.length && now - lastLaunch > 150) {
                var q = queue[launched++];
                lastLaunch = now;
                var inward = q.x + cw / 2 > W / 2 ? -1 : 1;
                live.push({ c: q.c, x: q.x, y: q.y, vx: (seed.next() < 0.82 ? inward : -inward) * (1.6 + seed.next() * 3.4), vy: -(1 + seed.next() * 5) });
            }
            for (var n = live.length - 1; n >= 0; n--) {
                var o = live[n];
                o.vy += 0.42 * dt; o.x += o.vx * dt; o.y += o.vy * dt;
                if (o.y + ch > H) { o.y = H - ch; o.vy = -o.vy * 0.82; if (Math.abs(o.vy) < 1.2) o.vy = -1.2; }
                drawCard(o);
                if (o.x > W || o.x + cw < 0) live.splice(n, 1);
            }
            if ((launched >= queue.length && !live.length) || now - t0 > 14000) { end(); return; }
            fx.raf = requestAnimationFrame(frame);
        }
        fx.raf = requestAnimationFrame(frame);
    }

    // ── sound: three short tones, off unless asked for ──────────────────────
    var audio = null;
    function sound(kind) {
        if (!P.sound) return;
        try {
            if (!audio) audio = new (window.AudioContext || window.webkitAudioContext)();
            if (audio.state === 'suspended') audio.resume();
            var notes = kind === 'win' ? [523, 659, 784, 1047] : (kind === 'turn' ? [330] : [440]);
            notes.forEach(function (f, i) {
                var o = audio.createOscillator(), g = audio.createGain(), at = audio.currentTime + i * 0.11;
                o.type = 'triangle'; o.frequency.value = f;
                g.gain.setValueAtTime(0.0001, at);
                g.gain.exponentialRampToValueAtTime(0.09, at + 0.012);
                g.gain.exponentialRampToValueAtTime(0.0001, at + (kind === 'win' ? 0.3 : 0.09));
                o.connect(g); g.connect(audio.destination);
                o.start(at); o.stop(at + 0.35);
            });
        } catch (_) {}
    }

    // ── sheets ──────────────────────────────────────────────────────────────
    function openSheet(id) {
        if (sheetOpen) closeSheet(true);
        var el = $(id), modal = el.tagName === 'DIALOG';
        table.settle();
        sheetOpen = id;
        if (modal) {
            try { if (!el.open) el.showModal(); } catch (_) { el.setAttribute('open', ''); }
        } else {
            el.hidden = false;
            $('solScrim').hidden = false;
        }
        clockWasRunning = clock.running();
        if (clockWasRunning) clock.pause();
        var first = modal ? el.querySelector('[data-first]') : (el.querySelector('.primary') || el.querySelector('button'));
        try { if (first) first.focus({ preventScroll: true }); } catch (_) {}
        if (id === 'solWinSheet') {
            animate(el, [{ opacity: 0, transform: 'translate(-50%,-46%) scale(.94)' }, { opacity: 1, transform: 'translate(-50%,-50%) scale(1)' }], { duration: 320, easing: 'cubic-bezier(.2,.9,.3,1.2)' });
            var marks = el.querySelectorAll('.sol-win-mark svg');
            for (var i = 0; i < marks.length; i++) animate(marks[i], [{ transform: 'scale(.2)', opacity: 0 }, { transform: 'scale(1.25)', opacity: 1, offset: 0.6 }, { transform: 'scale(1)', opacity: 1 }], { duration: 420, delay: 160 + i * 90, easing: 'ease-out', fill: 'backwards' });
        }
        funnel('sheet_open', { sheet: id });
    }
    function closeSheet(keepClock) {
        if (!sheetOpen) return;
        var el = $(sheetOpen), was = sheetOpen;
        sheetOpen = null;
        if (el.tagName === 'DIALOG') { try { if (el.open) el.close(); } catch (_) { el.removeAttribute('open'); } }
        else { el.hidden = true; $('solScrim').hidden = true; }
        if (!keepClock && clockWasRunning && !over) clock.start();
        if (was === 'solNewSheet' && sess) paintChrome();
        try { tableEl.focus({ preventScroll: true }); } catch (_) {}
    }
    // ── the rules of the game on the table ──────────────────────────────────
    // Built when opened, for the game AND variant on the table, from the same
    // card faces the table draws (public/js/solitaire/rules.js).
    function openRules(via) {
        if (!sess) return;
        var body = $('solRulesBody');
        if (window.SolRules) {
            body.textContent = '';
            window.SolRules.build(body, {
                game: game, v: sess.state.v, state: sess.state, def: def, S: S,
                t: function (k) { return T[k] == null ? '' : String(T[k]); },
                face: table.face, rankText: rankText, chip: chip, number: sess.number,
                verdict: brain.verdict
            });
        }
        $('solRulesH').textContent = t('rules_title', { game: variantLabel() });
        body.scrollTop = 0;
        P.rules = Object.assign({}, P.rules || {});
        if (!P.rules[game]) { P.rules[game] = 1; writePrefs(); }
        $('solRulesBtn').classList.remove('nudge');
        openSheet('solRules');
        funnel('rules_open', { game: game, variant: sess.variantKey, via: via || 'button', steps: sess.steps() });
    }
    // A first game of a kind: when the person seems lost — a card that would not
    // move, or a long while with no move at all — the «Rules» button glows, once
    // per game and never again. The dialog itself never opens on its own.
    var nudged = {}, idleNudge = 0;
    function nudgeRules(why) {
        if (!sess || nudged[game] || (P.rules && P.rules[game]) || sheetOpen) return;
        nudged[game] = 1;
        P.rules = Object.assign({}, P.rules || {});
        P.rules[game] = 1;
        writePrefs();
        var b = $('solRulesBtn');
        b.classList.remove('nudge');
        void b.offsetWidth;
        b.classList.add('nudge');
        setTimeout(function () { b.classList.remove('nudge'); }, 4000);
        funnel('rules_nudge', { game: game, why: why });
    }
    function armIdleNudge() {
        clearTimeout(idleNudge);
        if (P.rules && P.rules[game]) return;
        var g = game, n = sess ? sess.number : 0;
        idleNudge = setTimeout(function () {
            if (sess && game === g && sess.number === n && !sess.steps() && document.visibilityState === 'visible') nudgeRules('idle');
        }, 25000);
    }

    function openNew() {
        var isK = game === 'klondike', v = variantOf(game);
        $('solKOpts').hidden = !isK;
        if (isK) { pressOnly('solPasses', String(v.passes)); pressOnly('solScoring', v.score); }
        $('solSure').checked = !!P.sure;
        $('solNum').value = sess ? String(sess.number) : '';
        $('solNumWarn').hidden = true;
        $('solUnfinished').hidden = !(sess && touched && everMoved() && !over);
        var key = GK.dailyKey(), vk = sess ? sess.variantKey : '', res = stats.dailyResult(key, vk), streak = stats.dailyStreak(key, vk);
        var label = t('new_daily');
        if (res) label += ' — ' + t('daily_done', { time: GK.formatTime(res.ms || 0) });
        else if (streak > 0) label += ' — ' + t('daily_streak', { n: streak });
        $('solDailyT').textContent = label;
        openSheet('solNewSheet');
    }
    function pressOnly(groupId, value) {
        var b = $(groupId).querySelectorAll('button');
        for (var i = 0; i < b.length; i++) b[i].setAttribute('aria-pressed', b[i].getAttribute('data-v') === value ? 'true' : 'false');
    }
    function fillStats(box, rows) {
        box.textContent = '';
        rows.forEach(function (r) {
            var d = document.createElement('div'), b = document.createElement('b'), s = document.createElement('span');
            d.className = 'sol-stat';
            b.textContent = r[0]; s.textContent = r[1];
            d.appendChild(b); d.appendChild(s);
            box.appendChild(d);
        });
    }
    function variantLabel() {
        var v = sess.state.v, name = t('g_' + game);
        if (game === 'klondike') return name + ', ' + t(v.draw === 3 ? 'v_draw3' : 'v_draw1') + (v.score === 'vegas' ? ', ' + t('k_score_vegas') : '');
        if (game === 'spider') return name + ', ' + t('v_suits' + v.suits);
        return name;
    }
    function paintStats() {
        var s = stats.summary(sess.variantKey), rows = [];
        $('solStatsOf').textContent = variantLabel();
        $('solStatsEmpty').hidden = s.played > 0;
        $('solStats').hidden = !s.played;
        if (!s.played) return;
        rows.push([String(s.played), t('st_played')]);
        rows.push([String(s.won), t('st_won')]);
        rows.push([Math.round((s.winRate || 0) * 100) + '%', t('st_rate')]);
        if (s.bestMs != null) rows.push([GK.formatTime(s.bestMs), t('st_best_time')]);
        if (s.avgMs != null) rows.push([GK.formatTime(s.avgMs), t('st_avg_time')]);
        if (s.bestScore != null) rows.push([String(s.bestScore), t(game === 'freecell' ? 'st_fewest' : 'st_best_score')]);
        rows.push([String(s.streak), t('st_streak')]);
        rows.push([String(s.bestStreak), t('st_best_streak')]);
        fillStats($('solStats'), rows);
    }
    function openSettings() {
        $('solXl').checked = !!P.xl; $('solFour').checked = !!P.four;
        if ($('solRanks')) $('solRanks').checked = !!P.ranks;
        $('solTap').checked = !!P.tap; $('solVerdictOn').checked = !!P.verdict; $('solTargets').checked = !!P.targets;
        $('solLefty').checked = !!P.lefty; $('solTimer').checked = !!P.timer; $('solSound').checked = !!P.sound;
        pressOnly('solBacks', P.back); pressOnly('solTables', P.table); pressOnly('solAuto', P.auto);
        pressOnly('solSpeed', P.speed); pressOnly('solWinFx', P.win);
        resetArmed = false;
        $('solStatsReset').textContent = t('stats_reset');
        paintStats();
        openSheet('solSetSheet');
    }
    var resetArmed = false;

    function share() {
        var v = sess.state.v, q = '?game=' + game;
        if (game === 'klondike') q += '&draw=' + v.draw;
        if (game === 'spider') q += '&suits=' + v.suits;
        q += '&deal=' + sess.number;
        var url = location.origin + location.pathname + q;
        function ok() { toast(t('share_done'), 'success'); funnel('share', { game: game }); }
        function legacy() {
            try {
                var ta = document.createElement('textarea');
                ta.value = url; ta.setAttribute('readonly', '');
                ta.style.position = 'absolute'; ta.style.left = '-9999px';
                root.appendChild(ta); ta.select();
                var done = document.execCommand('copy');
                root.removeChild(ta);
                if (done) ok(); else toast(t('share_fail'), 'warning');
            } catch (e) { fail(e, 'solitaire_share'); }
        }
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(ok, legacy);
        else legacy();
    }

    // ── whole screen ────────────────────────────────────────────────────────
    function toggleFull() { immersive.toggle(); }
    function onFull(on, mode) {
        $('solFull').setAttribute('aria-pressed', on ? 'true' : 'false');
        $('solFullLbl').textContent = t(on ? 'exit_fullscreen' : 'fullscreen');
        var use = $('solFullIco').querySelector('use');
        if (use) use.setAttribute('href', on ? '#sol-i-min' : '#sol-i-full');
        funnel(on ? 'fullscreen_on' : 'fullscreen_off', { mode: mode || '', game: game, w: window.innerWidth, h: window.innerHeight });
        requestAnimationFrame(function () { table.relayout(); });
        setTimeout(function () { table.relayout(); }, 260);
    }

    // The stage is as tall as the window allows under the site's fixed header.
    // The header's height is not published anywhere (and is two rows on a
    // phone), so it is measured: how far down the top of the window is covered
    // by something fixed.
    function fitStage() {
        if (immersive && immersive.active()) return;
        var top = 0, limit = Math.min(360, window.innerHeight / 2), w = window.innerWidth, bars = [];
        // bars pinned to the top of the window, stacked one under another
        var cand = document.querySelectorAll('header, [class*="header"], [class*="banner"], [id*="banner"]');
        for (var c = 0; c < cand.length; c++) {
            if (root.contains(cand[c])) continue;
            var cs = getComputedStyle(cand[c]);
            if ((cs.position !== 'fixed' && cs.position !== 'sticky') || cs.display === 'none' || cs.visibility === 'hidden') continue;
            var r = cand[c].getBoundingClientRect();
            if (r.width >= w * 0.6 && r.height > 0 && r.height < 280 && r.top >= -2 && r.top < limit) bars.push(r);
        }
        bars.forEach(function (b) { if (b.bottom > top) top = Math.ceil(b.bottom); });
        // three columns of probes: a header has gaps (and parts that let clicks through)
        [0.2, 0.5, 0.8].forEach(function (fx) {
            for (var y = 2; y < limit; y += 6) {
                var node = document.elementFromPoint(Math.floor(w * fx), y);
                for (; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
                    if (node === root) break;
                    var pos = getComputedStyle(node).position;
                    if (pos === 'fixed' || pos === 'sticky') { if (y + 6 > top) top = y + 6; break; }
                }
            }
        });
        root.style.setProperty('--sol-top', top + 'px');
    }

    // ── wiring ──────────────────────────────────────────────────────────────
    function wire() {
        $('solGames').addEventListener('click', function (e) {
            var b = e.target.closest('button[data-game]');
            if (b) switchGame(b.getAttribute('data-game'));
        });
        $('solVars').addEventListener('click', function (e) {
            var b = e.target.closest('button[data-var]');
            if (b) setVariant(b.getAttribute('data-var'));
        });
        $('solDeal').addEventListener('click', openNew);
        $('solNew').addEventListener('click', openNew);
        $('solUndo').addEventListener('click', function () { undo(false); });
        $('solRedo').addEventListener('click', redo);
        $('solHint').addEventListener('click', hint);
        $('solFull').addEventListener('click', toggleFull);
        $('solSetBtn').addEventListener('click', openSettings);
        $('solRulesBtn').addEventListener('click', function () { openRules('button'); });
        var dlg = $('solRules');
        // Esc and the browser's own closing of a modal dialog: the page's bookkeeping follows
        dlg.addEventListener('close', function () { if (sheetOpen === 'solRules') closeSheet(); });
        dlg.addEventListener('click', function (e) {
            if (e.target.closest('[data-close-rules]')) { closeSheet(); return; }
            // a press on the dimmed page around the dialog closes it
            if (e.target === dlg) {
                var r = dlg.getBoundingClientRect();
                if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeSheet();
            }
        });
        $('solFinish').addEventListener('click', finishGame);
        $('solRewind').addEventListener('click', rewind);
        $('solNoteUndo').addEventListener('click', function () { undo(true); });
        $('solNoteRestart').addEventListener('click', restartDeal);
        $('solNoteNew').addEventListener('click', openNew);
        $('solNote').addEventListener('click', function (e) {
            if (e.target.closest('button')) return;
            noteHidden = $('solNote')._tag || '';
            $('solNote').hidden = true;
        });
        $('solWaitSkip').addEventListener('click', skipFinding);

        $('solDealNew').addEventListener('click', function () { newGame('sheet'); });
        $('solRestart').addEventListener('click', restartDeal);
        $('solDaily').addEventListener('click', playDaily);
        $('solNumGo').addEventListener('click', function () { playNumber($('solNum').value.replace(/[^\d]/g, '')); });
        $('solNum').addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); playNumber($('solNum').value.replace(/[^\d]/g, '')); } });
        $('solShare').addEventListener('click', share);
        $('solSure').addEventListener('change', function () { P.sure = $('solSure').checked; writePrefs(); });
        chips('solPasses', function (v) { P.variants.klondike = S.games.klondike.variant({ draw: P.variants.klondike.draw, passes: +v, score: P.variants.klondike.score }); writePrefs(); });
        chips('solScoring', function (v) {
            var k = P.variants.klondike, passes = k.passes;
            // Vegas is played with a limited stock: one pass turning one card, three turning three.
            if (v === 'vegas' && !passes) passes = k.draw === 3 ? 3 : 1;
            P.variants.klondike = S.games.klondike.variant({ draw: k.draw, passes: passes, score: v });
            pressOnly('solPasses', String(P.variants.klondike.passes));
            writePrefs();
        });
        $('solWinNew').addEventListener('click', function () { newGame('win'); });
        $('solWinSame').addEventListener('click', restartDeal);

        bindCheck('solXl', 'xl', function () { applyPrefs(); table.relayout(); });
        bindCheck('solFour', 'four', applyPrefs);
        if ($('solRanks')) bindCheck('solRanks', 'ranks', function () { table.repaintFaces(); });
        bindCheck('solTap', 'tap');
        bindCheck('solVerdictOn', 'verdict', function () { applyPrefs(); think(); });
        bindCheck('solTargets', 'targets', applyPrefs);
        bindCheck('solLefty', 'lefty', function () { applyPrefs(); table.relayout(); });
        bindCheck('solTimer', 'timer', applyPrefs);
        bindCheck('solSound', 'sound', function () { sound('place'); });
        chips('solBacks', function (v) { P.back = v; writePrefs(); applyPrefs(); });
        chips('solTables', function (v) { P.table = v; writePrefs(); applyPrefs(); });
        chips('solAuto', function (v) { P.auto = v; writePrefs(); if (sess) sess.meta.auto = v; });
        chips('solSpeed', function (v) { P.speed = v; writePrefs(); applyPrefs(); });
        chips('solWinFx', function (v) { P.win = v; writePrefs(); });
        $('solStatsReset').addEventListener('click', function () {
            if (!resetArmed) { resetArmed = true; $('solStatsReset').textContent = t('stats_reset_sure'); return; }
            stats.reset(sess.variantKey);
            resetArmed = false;
            $('solStatsReset').textContent = t('stats_reset');
            paintStats();
        });

        root.addEventListener('click', function (e) {
            if (e.target.closest('[data-close]') || e.target === $('solScrim')) closeSheet();
        });
        // Esc closes a sheet before anything else hears it (the whole-screen overlay also listens for Esc).
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && sheetOpen) { e.stopImmediatePropagation(); e.preventDefault(); closeSheet(); }
        }, true);
        root.addEventListener('keydown', onKey);

        document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') persist(true); });
        window.addEventListener('pagehide', function () { persist(true); });
        // WebKit does not always get to pagehide on a reload, and a killed tab never does.
        window.addEventListener('beforeunload', function () { persist(true); });

        var pend = 0;
        function fit() { if (pend) return; pend = requestAnimationFrame(function () { pend = 0; table.relayout(); }); }
        window.addEventListener('resize', fitStage);
        // On a phone the stage fills the window, and the site's floating chat
        // button lands on its corner button. While the table is what is on
        // screen, the button steps aside (it comes back by itself on an unread
        // reply — docs/why/primitives/immersive.md).
        if (window.IntersectionObserver && typeof window.__immersive === 'function') {
            var held = null;
            new IntersectionObserver(function (list) {
                var e = list[list.length - 1], narrow = window.innerWidth <= 640;
                var on = narrow && e.isIntersecting && e.intersectionRatio >= 0.85;
                if (on && !held) held = window.__immersive('solitaire-table');
                else if (!on && held) { held(); held = null; }
            }, { threshold: [0, 0.5, 0.85, 1] }).observe(root);
        }
        // The header gets its last row (and its fonts) after this script has run.
        window.addEventListener('load', fitStage);
        var hdr = document.querySelector('.header');
        if (hdr && window.ResizeObserver) new ResizeObserver(fitStage).observe(hdr);
        if (window.ResizeObserver) new ResizeObserver(fit).observe(tableEl);
        else window.addEventListener('resize', fit);
    }
    function bindCheck(id, key, then) {
        $(id).addEventListener('change', function () {
            P[key] = $(id).checked;
            writePrefs();
            if (then) then();
        });
    }
    function chips(groupId, onPick) {
        $(groupId).addEventListener('click', function (e) {
            var b = e.target.closest('button[data-v]');
            if (!b) return;
            pressOnly(groupId, b.getAttribute('data-v'));
            onPick(b.getAttribute('data-v'));
        });
    }

    // ── start ───────────────────────────────────────────────────────────────
    function boot() {
        S = window.Solitaire; GK = window.GameKit;
        root = $('sol'); tableEl = $('solTable');
        P = readPrefs();
        table = window.SolTable.create(tableEl, {
            rankText: rankText,
            speedMs: speedMs,
            canLift: canLift,
            targets: targetsOf,
            onTap: function (p, n, e) { onTap(p, n, e, 'tap'); },
            onSlot: function (p, e) { onSlot(p, e, 'tap'); },
            onFelt: function () { if (picked) { setPicked(null); say(t('a_dropped')); } },
            onDrop: function (f, n, to, from) { return play({ k: 'm', f: f, t: to, n: n }, 'drag', { from: from }); },
            onDragStart: function () { table.clearHint(); setPicked(null); },
            onDragEnd: function (done, info) { if (!done && info && info.over >= 0) explain(info.p, info.n, info.over); },
            motion: motion,
            onLight: function (m) { funnel('motion_light', m); },
            onContext: collectAll
        });
        stats = GK.stats(SLUG);
        function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }
        var clockSavedAt = 0;
        clock = GK.clock({ onTick: function (ms) {
            textWrite($('solTime'), GK.formatTime(ms));
            // the clock of a game in progress is written down every few seconds: a tab that
            // dies without a word loses at most that much of it
            if (touched && !over && ms - clockSavedAt >= 5000) { clockSavedAt = ms; persist(true); }
        } });
        immersive = GK.immersive(root, { key: SLUG, onChange: onFull });
        applyPrefs();
        fitStage();

        var q = null, wantGame = null, wantDeal = 0, linked = false;
        try { q = new URLSearchParams(location.search); } catch (_) {}
        if (q) {
            if (S.games[q.get('game')]) { wantGame = q.get('game'); linked = true; }
            if (q.get('draw')) { P.variants.klondike = S.games.klondike.variant({ draw: +q.get('draw'), passes: P.variants.klondike.passes, score: P.variants.klondike.score }); wantGame = wantGame || 'klondike'; linked = true; }
            if (q.get('suits')) { P.variants.spider = S.games.spider.variant({ suits: +q.get('suits') }); wantGame = wantGame || 'spider'; linked = true; }
            if (q.get('deal')) { wantDeal = +String(q.get('deal')).replace(/[^\d]/g, ''); linked = true; }
        }
        var last = load('last', null);
        var first = !last && !linked;
        game = wantGame || (S.games[last] ? last : 'klondike');
        def = S.games[game];
        wantDeal = S.cleanNumber(game, wantDeal);

        var saved = load('save_' + game, null), resumed = false;
        if (saved && saved.s && (!linked || sameAsSaved(saved.s, wantDeal))) resumed = restoreGame(game);
        if (!resumed) {
            if (first && CFG.welcome) {
                // The table the server rendered becomes the game: same number, same cards.
                startGame('klondike', CFG.welcome.number, { draw: CFG.welcome.draw }, { sure: true, instant: true, quiet: true });
                welcomeOn = true;
            } else if (wantDeal) {
                startGame(game, wantDeal, variantOf(game), { sure: isSure(game, wantDeal), via: 'link', touched: true, instant: true });
            } else {
                sess = null;
                newGame(linked ? 'link' : 'return');
            }
        }
        root.removeAttribute('data-boot');
        wire();
        // Read-only window into the game for the browser stands (tests/playwright/solitaire-*.mjs).
        window.__solApp = {
            game: function () { return game; },
            state: function () { return sess ? sess.state : null; },
            number: function () { return sess ? sess.number : 0; },
            variantKey: function () { return sess ? sess.variantKey : ''; },
            steps: function () { return sess ? sess.steps() : 0; },
            won: function () { return !!(sess && sess.won()); },
            over: function () { return over; },
            meta: function () { return sess ? sess.meta : null; },
            verdict: function () { return brain.verdict; },
            line: function () { return brain.line.slice(); },
            asking: function () { return !!brain.asking; },
            okAt: function () { return okAt; },
            elapsed: function () { return clock.elapsed(); },
            inputs: function () { return inputs; },
            motion: function () { return { kind: table.motionKind(), light: table.isLight(), busy: table.busy(), mode: motion() }; }
        };
        if (window.__stage) try { window.__stage('ready'); } catch (_) {}
        if (resumed) say(t('resumed'));

        // A press made before this script woke up.
        if (window.__earlyAction) {
            window.__earlyAction.claim(SLUG, function (a) {
                var m = String(a || '').split(':');
                if (m[0] === 'tap') { if (welcomeOn) onTap(+m[1], 1, null, 'tap'); }
                else if (m[0] === 'stock') { if (welcomeOn) doStock('tap'); }
                else if (m[0] === 'new') openNew();
                else if (m[0] === 'hint') hint();
                else if (m[0] === 'full') toggleFull();
                else if (m[0] === 'settings') openSettings();
                else if (m[0] === 'rules') openRules('early');
                else if (m[0] === 'game') switchGame(m[1]);
                else if (m[0] === 'var') setVariant(m[1]);
            });
        }
    }
    function sameAsSaved(s, wantDeal) {
        if (!s) return false;
        if (wantDeal && s.n !== wantDeal) return false;
        var want = S.games[game].variantKey(S.games[game].variant(variantOf(game)));
        var have = S.games[game].variantKey(S.games[game].variant(s.o));
        return want === have;
    }

    function ready() { return window.Solitaire && window.SolTable && window.GameKit && window.__toolSettings && $('sol'); }
    function go() {
        if (ready()) { try { boot(); } catch (e) { try { if (window.__handleError) window.__handleError(e, 'solitaire_boot'); } catch (_) {} $('sol').removeAttribute('data-boot'); } return; }
        // a helper that came from the fallback source arrives later than this script
        var tries = 0, iv = setInterval(function () {
            if (ready() || ++tries > 100) {
                clearInterval(iv);
                if (ready()) go();
            }
        }, 100);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go); else go();
})();
